import {
  Box, Button, Card, Link, List, ListItem, Table, TableBody, TableCell, TableContainer, TableRow, Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import axios from 'axios';
import base_url from '../../../../../baseUrls';
import {FormProvider, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import * as Yup from 'yup';
import * as allConstants from '../../../../../constants/allConstants';
import {useJob} from '../../../../../contextProviders/jobContext';
import {useEditRecord} from '../../../../../hooks/useEditRecord';
import {useSubform} from '../../../../../hooks/useSubform';
import {AddRowButton, variants} from '../../../../../pieces/addDeleteEquipmentRowButtons';
import FormSubmitButtons from '../../../../../pieces/formSubmitButtons';
import CustomFormBlock from '../../../../../pieces/inputs/formBlock';
import {inputTypes} from '../../../../../pieces/inputs/formInput';
import {InspectionRequirementsForContractor} from '../../../../../pieces/inspectionRequirementsForContractor';
import {NoDataStringWrapper} from '../../../../../pieces/noDataStringWrapper';
import Spacer from '../../../../../pieces/spacer';
import {CustomTable} from '../../../../../pieces/table';
import LadderIcon from '../../../../../static/images/ladder.png';
import {globalUseClasses} from '../../../../../styles/globalClasses';
import ApiUtils from '../../../../../utils/apiUtils';
import Utils from '../../../../../utils/utils';
import {WrappedHomeOwnerInfoRows} from '../../../components/homeownerInfoBlock';
import {SMS_TEMPLATE_NAMES, useGetSms} from '../../../../../hooks/useGetSms';
import {JobActionButtonsContained} from '../../../../../pieces/jobActionButtons';
import {useAutocompleteOptions} from '../../../../../hooks/useAutompleteOptions';
import {NewPermissionGate} from '../../../../../permissions/permissionGate';
import {
  ELEMENTS_INSPECTIONS,
  inspectionRules
} from '../../../../../permissions/ipermitAccessControl/inspectionsAccessControl';
import {useGotIt} from '../../../../../hooks/useGotIt';


const InspectionAiCallLogs = (props)=> {
  const classes = {...globalUseClasses()};
  const gotItPopup = useGotIt();
  const user = useSelector(state=>state.user);
  const {job, setJob} = useJob();
  const {INSPECTION_FIELDS,
    JOB_FIELDS, CALL_LOGS_FIELDS,
    CALL_RESULTS_CITY,
    CALL_RESULTS_HOME_OWNER,
    CALL_TO_OPTIONS,
    INSPECTION_TYPES,
    SCHEDULING_FIELDS,
  } = allConstants;

  const options = {
    cityOptions: useAutocompleteOptions({field: 'cityOptions'}).options,
  };
  const [currentCalls, setCurrentCalls] = useState(job?.[INSPECTION_FIELDS.INSPECTION.api_name]?.[CALL_LOGS_FIELDS.CALL_LOG.api_name] || []);
  const [addingCallTo, setAddingCallTo] = useState([]);
  const isAiJobCallsFetchingRef = useRef(false);
  const [aiJobCalls, setAiJobCalls] = useState([]);
  const [aiJobCallsLoading, setAiJobCallsLoading] = useState(true);
  const [aiJobCallsError, setAiJobCallsError] = useState('');
  const jobNumber = job?.[JOB_FIELDS.JOB_NUMBER.api_name];

  useEffect(()=> {
    setCurrentCalls(job?.[INSPECTION_FIELDS.INSPECTION.api_name]?.[CALL_LOGS_FIELDS.CALL_LOG.api_name] || []);
  }, [job]);


  const collectionName = useMemo(()=> CALL_LOGS_FIELDS.CALL_LOG.api_name, []);

  const validationSchema = useMemo(()=>Yup.object().shape({
    //[INSPECTION_FIELDS.REQUIREMENTS.api_name]: Yup.string().required(allConstants.ERROR_MSG.REQUIRED_FIELD).nullable(),
    [collectionName]: Yup.array().of(Yup.object().shape({
      [CALL_LOGS_FIELDS.CALL_RESULT.api_name]: Yup.string().required(allConstants.ERROR_MSG.REQUIRED_FIELD).nullable(),
      [CALL_LOGS_FIELDS.TYPE.api_name]: Yup.string().required(allConstants.ERROR_MSG.REQUIRED_FIELD).nullable(),
    }))
  }), []);

  const collectionRow = useMemo(()=>{return {
    [CALL_LOGS_FIELDS.DATE.api_name]: '',
    [CALL_LOGS_FIELDS.TYPE.api_name]:null,
    [CALL_LOGS_FIELDS.CALL_RESULT.api_name]:null,
    [CALL_LOGS_FIELDS.LOGGED_BY.api_name]: null,
    [CALL_LOGS_FIELDS.NOTE.api_name]:''
  };}, []);

  const {getValue, commonFieldProps, fields, formMethods,
    addRowAction, getDeleteRowButton, replace} = useSubform({
    collectionName: collectionName,
    validationSchema: validationSchema,
    subformRow: collectionRow,
    currentResults: [],
    addRowText: 'Add call'
  });

  const addCall = (callTo)=> {
    const addCallTo = [...addingCallTo];
    addCallTo.push(callTo);
    setAddingCallTo(addCallTo);
    addRowAction();
  };

  const  {edit, EditButton, cancelEdit} = useEditRecord();
  const textHomeownerForAvailability = useGetSms(
    {
      templateName: SMS_TEMPLATE_NAMES.ASK_HOMEOWNER_AVAILABILITY,
      data: {
        jobNumber: job?.[JOB_FIELDS.JOB_NUMBER.api_name],
        company: job?.company?.[allConstants.COMPANY_FIELDS.NAME.api_name]?.toUpperCase(),
        address: Utils.getAddressString(job?.[JOB_FIELDS.GENERAL.ADDRESS.api_name]),

      }
    }
  );

  const userAllowedToEditInspection = useMemo(()=> {
    return NewPermissionGate({
      user: user,
      elementPermissions: inspectionRules[ELEMENTS_INSPECTIONS.manageInspection],
    });
  }, [user]);


  useEffect(()=> {
    if(edit && !userAllowedToEditInspection) {
      gotItPopup.setShow(allConstants.ERROR_MSG.NO_PERMISSION('edit inspection'));
      cancelEdit();
      return;
    }
    replace([]);
    editInspectionForm.reset(job?.[INSPECTION_FIELDS.INSPECTION.api_name]);

  },[edit]);

  useEffect(()=> {
    if(!edit) {
      const currentCallsCount = currentCalls?.length || 0;
      if(addingCallTo.length > currentCallsCount) {
        if (currentCallsCount > 0) {
          const calls = [...addingCallTo];
          calls.splice(currentCallsCount - 1, addingCallTo.length - currentCallsCount);
          setAddingCallTo(calls);
        } else {
          setAddingCallTo([]);
        }
      }
    }
  }, [edit]);

  const inspectionFormCommonProps = {
    fullWidth: true,
    maxWidth: '100%',
    isEditable: true,
    renderLabel: true,
    viewMode: !edit
  };

  if(Utils.isDevEnv()) {
    window.aiInspectionCallLogs = formMethods;
  }

  const getCurrentCallType = (index)=> {
    return addingCallTo[index];
  };

  const createNameString = (name)=> {
    if(!name) return '';
    return `${name[allConstants.USER_FIELDS.FIRST_NAME.api_name]} ${name[allConstants.USER_FIELDS.LAST_NAME.api_name]}`;
  };

  const handleSubmitResponse = (result, message, key)=> {
    const toastOptions = {
      toastId: 'inspection_response',
      autoClose: 1000,
    };
    setAddingCallTo([]);


    if(result.status === 'success') {
      const calls = result?.data?.[CALL_LOGS_FIELDS.CALL_LOG.api_name];

      try{
        const returnedInspectionData = result.data?.[INSPECTION_FIELDS.INSPECTION.api_name];
        let updatedInspection = {...job[INSPECTION_FIELDS.INSPECTION.api_name]};
        if(returnedInspectionData) {
          updatedInspection = {...updatedInspection, ...returnedInspectionData};
        }
        if(calls){
          updatedInspection[CALL_LOGS_FIELDS.CALL_LOG.api_name] = calls;
        }

        setJob({...job, ...{[allConstants.INSPECTION_FIELDS.INSPECTION.api_name]: updatedInspection}});
      } catch(e) {
        console.log('Error on handling update inspection and call logs', e);
      }

      // formMethods.reset(result?.data?.[INSPECTION_FIELDS.INSPECTION.api_name]);
      cancelEdit();
      toast.success(message ? message : 'Inspection updated', toastOptions);
    } else{
      toast.error(`Inspection update error: ${result?.message}`, toastOptions);
    }
  };

  const gridTemplateColumns = props.isContractor ? '100px 1fr 1.5fr 1.5fr 3fr':'170px 200px 1fr 40px';
  const gap = '16px';

  const getCallOptions = (index)=> {
    const callTo = addingCallTo[index];
    if(callTo === CALL_TO_OPTIONS.HOME_OWNER.value) {
      return Utils.getOptions(CALL_RESULTS_HOME_OWNER);
    } else {
      return Utils.getOptions(CALL_RESULTS_CITY);
    }
  };

  const editInspectionForm = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode:'all',
  });


  if(Utils.isDevEnv()) {
    window.editAiInspection = editInspectionForm;
  }

  const getInspectionValueFor = (field, defaultValue)=> {
    const inspection = job?.[INSPECTION_FIELDS.INSPECTION.api_name];
    return inspection?.[field.api_name] || defaultValue;
  };

  const resolveAllPromises = (promises)=> {
    const errors = [];
    Promise.all(promises).then((results)=> {
      let updateData = {};
      results.forEach((result)=> {
        if(result.status === 'success') {
          updateData = {...updateData, ...result.data};
        }
        else {
          errors.push(result.message);
        }
      });
      if(Object.keys(updateData).length > 0) {
        handleSubmitResponse({status: 'success', data: updateData}, 'Inspection updated');
      } else {
        handleSubmitResponse({status: 'error', message: errors.join(', ')}, errors.join(', '));
      }

    });
  };

  const onSubmit = async (data)=> {
    if(props.isContractor) return;
    let promises = [];
    const jobId = job?.[JOB_FIELDS.ID.api_name];
    const addedRows = data?.[collectionName];

    const inspectionData = editInspectionForm.getValues();
    inspectionData.id = jobId;

    // Thanh 02/04/24
    //console.log('call logs Panel');
    //console.log('inspection data', inspectionData);
    //console.log('data');
    //console.log(data);


    promises.push(ApiUtils.makeApiCall('POST', 'ipermit/jobs/city-inspection/edit-inspection', JSON.stringify(inspectionData)).then(
      (result)=> {return result;}
    ));

    if(addedRows.length > 0) {

      //console.log('new calls');
      //console.log(addedRows);
      
      
      promises.push(ApiUtils.makeApiCall('POST', 'ipermit/jobs/city-inspection/add-inspection-calls', JSON.stringify({
        id: jobId,
        calls: addedRows,
      })).then(
        (result)=> {return result;}
      ));
    }
    resolveAllPromises(promises);
  };

  const getCallFieldValue = (fieldName, index, returnValue)=> {
    if(props.isContractor){
      return job?.[INSPECTION_FIELDS.INSPECTION.api_name]?.[collectionName]?.[index]?.[fieldName];
    }
    return returnValue ? returnValue : getValue(fieldName, index);
  };

  const onRowDelete = (index)=> {
    const calls = [...addingCallTo];
    calls.splice(index, 1);
    setAddingCallTo(calls);
  };

  const toggleInspectionTo = ()=> {
    return getInspectionValueFor(allConstants.INSPECTION_FIELDS.TYPE) === allConstants.INSPECTION_TYPES.ROUGH.value
      ? allConstants.INSPECTION_TYPES.FINAL.value : allConstants.INSPECTION_TYPES.ROUGH.value;
  };

  const changeInspectionType = async ()=> {
    if(!userAllowedToEditInspection){
      gotItPopup.setShow(allConstants.ERROR_MSG.NO_PERMISSION('edit inspection type'));
      return;
    }
    const inspectionData = {
      id: job?.[JOB_FIELDS.ID.api_name],
    };
    inspectionData[allConstants.INSPECTION_FIELDS.TYPE.api_name] = toggleInspectionTo();
    let promises = [];
    promises.push(ApiUtils.makeApiCall('POST', 'ipermit/jobs/city-inspection/edit-inspection', JSON.stringify(inspectionData)).then(
      (result)=> {return result;}
    ));
    resolveAllPromises(promises);

  };

  const createCallLogTableData = ()=> {
    const pastCalls = [...currentCalls].reverse();
    const header = [
      CALL_LOGS_FIELDS.DATE.label,
      CALL_LOGS_FIELDS.TYPE.label,
      CALL_LOGS_FIELDS.RESULT.label,
      CALL_LOGS_FIELDS.LOGGED_BY.label,
      CALL_LOGS_FIELDS.NOTE.label
    ];
    const data = pastCalls.map((row)=> {
      return [
        Utils.formatIsoDateString(row[CALL_LOGS_FIELDS.DATE.api_name]),
        row[CALL_LOGS_FIELDS.TYPE.api_name],
        row[CALL_LOGS_FIELDS.CALL_RESULT.api_name],
        createNameString(row[CALL_LOGS_FIELDS.LOGGED_BY.api_name]),
        row[CALL_LOGS_FIELDS.NOTE.api_name]
      ];
    });
    const columnWidths = [190, 150, 200, 230, null];
    return {
      tableHeader: header,
      tableData: data,
      columnWidths: columnWidths
    };
  };

  const deleteCallRow = (index)=> {
    console.log('will delete row', index);
  };

  const editCallRowAction = (index)=> {
    console.log('will edit row', index);
  };

  const canChangeInspectionType = ()=> {
    //don't allow to change inspection type when inspection is scheduled
    const currentStatus = job?.[INSPECTION_FIELDS.INSPECTION.api_name]?.[INSPECTION_FIELDS.INSPECTION_STATUS.api_name];
    return currentStatus !== allConstants.INSPECTION_STATUSES.SCHEDULED.value;
  };

  const getRaterName = ()=> {
    const inspectionData = job?.[INSPECTION_FIELDS.INSPECTION.api_name] || {};
    return inspectionData?.rater?.name || '';
  };

  const buildInspectionModuleUrl = (inspectionId) => {
    const idString = String(inspectionId ?? '').trim();
    if (!idString) {
      return '';
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const id = encodeURIComponent(idString);
    return `${origin}/module/${id}?moduleName=inspection&items=50&page=1&showSameAddressJobs=true`;
  };

  const formatUpdatedAt = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return String(value);
    }
    const mm = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const dd = String(parsedDate.getDate()).padStart(2, '0');
    const yy = String(parsedDate.getFullYear()).slice(-2);
    let hour12 = parsedDate.getHours() % 12;
    hour12 = hour12 === 0 ? 12 : hour12;
    const hh = String(hour12).padStart(2, '0');
    const min = String(parsedDate.getMinutes()).padStart(2, '0');
    const ampm = parsedDate.getHours() >= 12 ? 'pm' : 'am';
    return `${mm}/${dd}/${yy} ${hh}:${min} ${ampm}`;
  };

  const formatCallDetailSpeakerLabel = (speakerLabel) => {
    const lower = speakerLabel.toLowerCase();
    if (lower === 'user:') {
      return 'HO:  ';
    }
    if (lower === 'assistant:') {
      return 'Agent:  ';
    }
    if (lower === 'agent-action:') {
      return 'Agent Action:  ';
    }
    return speakerLabel;
  };

  const renderCallDetails = (callDetails) => (
    String(callDetails).split('\n').map((line, index) => {
      const matchedLine = line.match(/^(\s*)(user:|assistant:|agent-action:)(.*)$/i);
      if (!matchedLine) {
        return (
          <Box key={`${line}-${index}`} component="span" sx={{display: 'block'}}>
            {line}
          </Box>
        );
      }
      const [, leadingWhitespace, speakerLabel, remainingText] = matchedLine;
      const isUserLine = speakerLabel.toLowerCase() === 'user:';
      return (
        <Box
          key={`${line}-${index}`}
          component="span"
          sx={{
            backgroundColor: isUserLine ? '#e3f2fd' : 'transparent',
            borderRadius: '4px',
            display: 'block',
            px: isUserLine ? 0.5 : 0,
          }}
        >
          {leadingWhitespace}
          <Box component="span" sx={{fontWeight: 700}}>
            {formatCallDetailSpeakerLabel(speakerLabel)}
          </Box>
          {remainingText}
        </Box>
      );
    })
  );

  const fetchAiCallsByJobNumber = useCallback(async ({showLoader = true} = {}) => {
  
    if (props.isContractor) {
      setAiJobCalls([]);
      if (showLoader) {
        setAiJobCallsLoading(false);
      }
      setAiJobCallsError('');
      return;
    }
    if (jobNumber === null || jobNumber === undefined || String(jobNumber).trim() === '') {
      setAiJobCalls([]);
      if (showLoader) {
        setAiJobCallsLoading(false);
      }
      setAiJobCallsError('');
      return;
    }
    if (isAiJobCallsFetchingRef.current) {
      return;
    }
    isAiJobCallsFetchingRef.current = true;
    if (showLoader) {
      setAiJobCallsLoading(true);
    }
    setAiJobCallsError('');
    try {
      const authToken = await ApiUtils.getAccessToken();
      if (!authToken) {
        return;
      }
      const response = await axios.get(`${base_url.api}ai/calls-by-job-number`, {
        headers: {Authorization: authToken},
        params: {jobNumber: String(jobNumber).trim()},
      });

      const data = response?.data?.data;
      const list = Array.isArray(data?.aiCalls) ? data.aiCalls
        : (Array.isArray(data?.calls) ? data.calls : (Array.isArray(data) ? data : []));
      setAiJobCalls(list);
    } catch (error) {
      setAiJobCallsError('Unable to load AI calls for this job.');
      setAiJobCalls([]);
    } finally {
      if (showLoader) {
        setAiJobCallsLoading(false);
      }
      isAiJobCallsFetchingRef.current = false;
    }
  }, [jobNumber, props.isContractor]);

  useEffect(() => {
    if (props.isContractor) {
      setAiJobCallsLoading(false);
      return;
    }
    const fetchKey = props.fetchKey ?? 0;
    if (!fetchKey) {
      setAiJobCallsLoading(false);
      return;
    }
    void fetchAiCallsByJobNumber({showLoader: true});
  }, [fetchAiCallsByJobNumber, props.isContractor, props.fetchKey]);

  const renderRecentCallItem = (item) => {
    const isCompleted = item.status?.toLowerCase() === 'completed';
    const displayStatus = isCompleted
    ? 'Call Made'
    : item.status
      ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
      : item.status;

    const rawInspectionJobId =
      item.inspection_job_id ??
      item.inspectionJobId ??
      item.inspection_id ??
      item.inspectionId;
    const inspectionJobIdStr =
      rawInspectionJobId == null || rawInspectionJobId === ''
        ? ''
        : String(rawInspectionJobId).trim();
    const hasInspectionJobLink = inspectionJobIdStr.length > 0;
    return (
      <Box
        className="e-card e-card-horizontal"
        sx={{
          backgroundColor: isCompleted ? '#e8f5e9' : '#fff3e0',
          border: '2px solid #d0d7de',
          borderRadius: '12px',
          boxSizing: 'border-box',
          minWidth: 0,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <Box
          sx={{
            alignItems: 'flex-start',
            borderBottom: '2px solid #d0d7de',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            px: 1.5,
            py: 0,
          }}
        >
          <TableContainer
            component={Box}
            sx={{width: '100%', mb: 0, overflowX: 'auto'}}
          >
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                borderCollapse: 'collapse',
                '& .MuiTableCell-root': {
                  borderBottom: 'none',
                  borderColor: 'divider',
                  fontSize: '0.95rem',
                  padding: '2px 8px',
                  verticalAlign: 'top',
                  wordBreak: 'break-word',
                  width: '25%',
                },
              }}
            >
              <TableBody>
                <TableRow>
                  <TableCell>
                    <Typography
                      component="div"
                      sx={{fontSize: '0.95rem', wordBreak: 'break-word', color: 'text.primary', fontWeight: 400}}
                    >
                      <Box component="span" sx={{fontWeight: 700}}>Status</Box>
                      <Box component="span" >: </Box>
                      <Box component="span" >{displayStatus}</Box>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="div"
                      sx={{fontSize: '0.95rem', wordBreak: 'break-word', color: 'text.primary', fontWeight: 400}}
                    >
                      <Box component="span" sx={{fontWeight: 700}}>Result</Box>
                      <Box component="span" >: </Box>
                      <Box component="span" >{item.result}</Box>
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      component="div"
                      sx={{fontSize: '0.95rem', wordBreak: 'break-word', color: 'text.primary', fontWeight: 400}}
                    >
                      <Box component="span" sx={{fontWeight: 700}}>Call Made at</Box>
                      <Box component="span" >: </Box>
                      <Box component="span" >{formatUpdatedAt(item.updated_at ?? item.updatedAt)}</Box>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box sx={{backgroundColor: '#ffffff', px: 2, py: 1.5}}>
          <Box sx={{width: '100%'}}>
            <TableContainer component={Box} sx={{width: '100%', overflowX: 'auto'}}>
              <Table
                size="small"
                sx={{
                  tableLayout: 'auto',
                  width: '100%',
                  borderCollapse: 'collapse',
                  '& .MuiTableCell-root': {
                    borderBottom: 'none',
                    borderColor: 'divider',
                    color: 'text.secondary',
                    fontSize: '0.95rem',
                    padding: '2px 0',
                    verticalAlign: 'top',
                    wordBreak: 'break-word',
                    textAlign: 'left',
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell>
                      {item.callDetails ? (
                        <Box>{renderCallDetails(item.callDetails)}</Box>
                      ) : null}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>
    );
  };

  return(
    <>
      {gotItPopup.PopUp()}
      {props.isContractor && <>
        <InspectionRequirementsForContractor job={job} />
        <Spacer y={1} />
      </>
      }
      <Box>
        <WrappedHomeOwnerInfoRows/>
        <Box
          className="e-card e-card-vertical"
          sx={{marginRight: {xs: 0, sm: 0}, maxWidth: '100%', mt: 0, width: '100%'}}
        >
          <Box className="e-card-content" sx={{px: 0.5, py: 1, width: '100%'}}>
            {props.isContractor && (
              <Typography color="text.secondary" sx={{py: 1}}>
                AI call logs for this job are available to iPermit users on the inspection record.
              </Typography>
            )}
            {!props.isContractor && !(props.fetchKey ?? 0) && (
              <Typography color="text.secondary" sx={{py: 1}}>
                Open this tab in the sidebar to load AI calls for this job.
              </Typography>
            )}
            {!props.isContractor && (props.fetchKey ?? 0) > 0 && (
              <>
                {aiJobCallsLoading && (
                  <Typography>Loading recent AI calls...</Typography>
                )}
                {!aiJobCallsLoading && aiJobCallsError && (
                  <Typography color="error">{aiJobCallsError}</Typography>
                )}
                {!aiJobCallsLoading && !aiJobCallsError && (!jobNumber || String(jobNumber).trim() === '') && (
                  <Typography>No job number; cannot load AI calls.</Typography>
                )}
                {!aiJobCallsLoading && !aiJobCallsError && jobNumber && String(jobNumber).trim() !== '' && aiJobCalls.length === 0 && (
                  <Typography>No recent AI calls found.</Typography>
                )}
                {!aiJobCallsLoading && !aiJobCallsError && jobNumber && aiJobCalls.length > 0 && (
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: '1fr',
                      mt: 0,
                      width: '100%',
                    }}
                  >
                    {aiJobCalls.map((callItem, idx) => (
                      <Box key={String(callItem.id ?? callItem._id ?? `ai-call-${idx}`)} sx={{minWidth: 0, width: '100%'}}>
                        {renderRecentCallItem(callItem)}
                      </Box>
                    ))}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

InspectionAiCallLogs.propTypes = {
  isContractor: PropTypes.bool,
  fetchKey: PropTypes.number,
};

InspectionAiCallLogs.defaultProps = {
  isContractor: false,
  fetchKey: 0,
};

export default InspectionAiCallLogs;
